import { Clock, Calendar, MessageCircle, Phone, Video, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const ConsultationPage = () => {
  return (
    <div className="min-h-screen pt-32 pb-16" data-testid="consultation-page">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl sm:text-6xl font-bold mb-6" data-testid="consultation-heading">
            The Consultation Process
          </h1>
          <p className="text-lg text-[#8C8C8C] max-w-2xl mx-auto">
            No consultation = no locked expectations = friction. 
            We design every service to ensure clarity before execution.
          </p>
        </div>

        {/* Why Consultation */}
        <div className="bg-white p-8 md:p-12 rounded-lg shadow-sm mb-12">
          <h2 className="text-3xl font-bold mb-6">Why Consultation Matters</h2>
          <p className="text-[#8C8C8C] mb-6 leading-relaxed">
            Every client is unique. Your vision, timeline, budget, and expectations deserve dedicated attention. 
            The consultation ensures we're aligned before you invest, eliminating surprises and maximizing satisfaction.
          </p>
          <p className="text-[#8C8C8C] leading-relaxed">
            This isn't a sales call—it's a planning session where we listen, advise, and confirm we can deliver exactly what you envision.
          </p>
        </div>

        {/* What's Covered */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold mb-8">What's Covered in Your Consultation</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-[#F2EBE5] p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-3">Your Vision</h3>
              <p className="text-[#8C8C8C]">
                We'll discuss your inspiration, preferred style, and any specific looks you have in mind.
              </p>
            </div>
            <div className="bg-[#F2EBE5] p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-3">Timeline & Logistics</h3>
              <p className="text-[#8C8C8C]">
                Event date, start time, location, and any travel considerations.
              </p>
            </div>
            <div className="bg-[#F2EBE5] p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-3">Service Scope</h3>
              <p className="text-[#8C8C8C]">
                Number of people, hair vs. makeup, trials, and any additional requests.
              </p>
            </div>
            <div className="bg-[#F2EBE5] p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-3">Investment & Next Steps</h3>
              <p className="text-[#8C8C8C]">
                Clear pricing, deposit details, and confirmation of your booking.
              </p>
            </div>
          </div>
        </div>

        {/* Time Expectations */}
        <div className="bg-white p-8 md:p-12 rounded-lg shadow-sm mb-12">
          <div className="flex items-center gap-3 mb-6">
            <Clock className="w-8 h-8 text-[#D4AF37]" />
            <h2 className="text-3xl font-bold">Time Expectations</h2>
          </div>
          <p className="text-[#8C8C8C] mb-4 leading-relaxed">
            Most consultations last <strong>20-30 minutes</strong>. This is enough time to cover all essentials without overwhelming your schedule.
          </p>
          <p className="text-[#8C8C8C] leading-relaxed">
            If you need more time or have complex requirements (large bridal parties, destination logistics, production details), we'll adjust accordingly.
          </p>
        </div>

        {/* Consultation Methods */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold mb-8">Choose Your Consultation Method</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-[#F2EBE5] hover:border-[#D4AF37] transition-colors">
              <MessageCircle className="w-10 h-10 text-[#D4AF37] mb-4" />
              <h3 className="text-xl font-semibold mb-3">Text Consultation</h3>
              <p className="text-[#8C8C8C] text-sm">
                Quick, convenient, and perfect for straightforward inquiries.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-[#F2EBE5] hover:border-[#D4AF37] transition-colors">
              <Phone className="w-10 h-10 text-[#D4AF37] mb-4" />
              <h3 className="text-xl font-semibold mb-3">Phone Call</h3>
              <p className="text-[#8C8C8C] text-sm">
                Traditional and personal. Ideal for detailed discussions.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-[#F2EBE5] hover:border-[#D4AF37] transition-colors">
              <Video className="w-10 h-10 text-[#D4AF37] mb-4" />
              <h3 className="text-xl font-semibold mb-3">Video Call</h3>
              <p className="text-[#8C8C8C] text-sm">
                Face-to-face connection with visual references and style exploration.
              </p>
            </div>
          </div>
        </div>

        {/* What to Prepare */}
        <div className="bg-[#F2EBE5] p-8 md:p-12 rounded-lg mb-12">
          <h2 className="text-3xl font-bold mb-6">What to Prepare</h2>
          <ul className="space-y-3 text-[#8C8C8C]">
            <li className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-[#D4AF37] mt-1 flex-shrink-0" />
              <span>Your event date, time, and location</span>
            </li>
            <li className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-[#D4AF37] mt-1 flex-shrink-0" />
              <span>Number of people needing hair/makeup services</span>
            </li>
            <li className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-[#D4AF37] mt-1 flex-shrink-0" />
              <span>Inspiration photos or style references (optional but helpful)</span>
            </li>
            <li className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-[#D4AF37] mt-1 flex-shrink-0" />
              <span>Any special considerations (allergies, sensitivities, venue restrictions)</span>
            </li>
          </ul>
        </div>

        {/* Next Steps */}
        <div className="bg-white p-8 md:p-12 rounded-lg shadow-sm mb-12">
          <h2 className="text-3xl font-bold mb-6">After Your Consultation</h2>
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold mb-2">1. Custom Proposal</h3>
              <p className="text-[#8C8C8C]">
                You'll receive a detailed proposal with services, pricing, and timeline.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">2. Secure Your Date</h3>
              <p className="text-[#8C8C8C]">
                A 20% non-refundable deposit locks in your date and confirms your booking.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">3. Relax & Prepare</h3>
              <p className="text-[#8C8C8C]">
                We handle the details. You focus on enjoying your special day.
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Begin?</h2>
          <Link
            to="/inquiry"
            data-testid="consultation-cta-button"
            className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-white px-8 py-4 rounded-full text-base font-medium hover:bg-[#b89530] transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1"
          >
            Book Your Consultation
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ConsultationPage;